<template>
  <ion-page>
    <ion-content v-if="category" class="h-full">
      <round-header
        :title="category.name"
        :edit-mode="editMode"
        :database="icons"
        subtitle="Category"
        :image="category.icon"
        back="/home"
        image-type="icon"
        :theme="theme()"
        @toggle-edit-mode="editMode = !editMode"
        @pick-icon="setIcon"
        @input="setName($event)"
      >
        <!-- Activity Deletion Popup !-->
        <div v-if="showActivity" class="flex justify-center">
          <div
            class="bg-bg border-bg-neutral absolute bottom-20 z-20 flex max-w-[80%] flex-wrap rounded-2xl border-4"
          >
            <div class="font-fredoka my-2 w-full text-center text-gray-500">
              Activities of Today
            </div>
            <template v-for="score in category.scores.filter((e) => Score.isToday(e))">
              <template v-if="score.photoId">
                <directus-image
                  :key="'score-' + score.id"
                  :src="score.photoId"
                  class="w-12 cursor-pointer"
                  @click="removeScore(score)"
                />
              </template>
              <template v-for="task in (tasks as Task[]).filter((e) => e.id == score.task)" v-else>
                <directus-image
                  v-if="task"
                  :key="'score-' + score.id + '-' + task.id"
                  :src="task.icon"
                  class="w-12 cursor-pointer"
                  @click="removeScore(score)"
                />
              </template>
              <!-- TODO: Show shared tasks in activity !-->
            </template>

            <div class="font-fredoka my-2 w-full text-center text-gray-500">
              Click to remove entry
            </div>
          </div>
        </div>

        <!-- Daily Goal !-->
        <div
          class="font-outline-1 font-fredoka relative z-10 text-center text-xl text-gray-100 drop-shadow"
        >
          Daily Goal
        </div>
        <div ref="progressBarRef" class="flex justify-center">
          <TinyButton v-if="editMode" :src="ICON_MINUS" @click="addToDailyGoal(-1)" />
          <Fill
            class="h-8 w-64 cursor-pointer"
            :value="category.getScore()"
            :max="category.dailyGoal"
            :label="editMode ? category.dailyGoal : ''"
            @click="showActivity = !showActivity"
          />
          <TinyButton v-if="editMode" :src="ICON_PLUS" @click="addToDailyGoal(1)" />
        </div>
      </round-header>

      <!-- Content !-->
      <div id="container">
        <div
          v-for="animation in flyingTaskAnimations"
          :key="animation.id"
          class="pointer-events-none fixed z-50 text-4xl transition-all"
          :class="
            animation.phase === 'fly'
              ? 'duration-650 ease-[cubic-bezier(0.2,1.25,0.3,1)]'
              : 'duration-180 ease-in'
          "
          :style="{
            left: `${animation.phase === 'start' ? animation.startX : animation.endX}px`,
            top: `${animation.phase === 'start' ? animation.startY : animation.endY}px`,
            transform:
              animation.phase === 'start'
                ? 'translate(-50%, -50%) scale(1) rotate(0deg)'
                : animation.phase === 'fly'
                  ? 'translate(-50%, -50%) scale(0.92) rotate(-10deg)'
                  : 'translate(-50%, -50%) scale(1.28) rotate(8deg)',
            opacity: animation.phase === 'pop' ? 0 : 1,
          }"
        >
          <directus-image v-if="animation.isImage" :src="animation.icon" class="h-14 w-14" />
          <template v-else>{{ animation.icon }}</template>
        </div>
        <div class="flex flex-wrap justify-center">
          <!-- Tasks !-->
          <template v-if="editMode">
            <template v-for="id in category.taskIds">
              <template v-for="task in (tasks as Task[]).filter((e) => e.id == id)" :key="task.id">
                <category-button
                  :icon="task.icon"
                  class="shake m-4"
                  @click="$router.push('/task/' + category.id + '/' + task.id)"
                />
              </template>
            </template>
            <template v-for="id in category.sharedTaskIds">
              <template v-for="task in [Task.fetch(db, id)]">
                <template v-if="task">
                  <category-button
                    :key="'shared-' + task.value.id"
                    :icon="task.value.icon"
                    class="shake m-4"
                    @click="deleteSharedTask(id)"
                  >
                    <div class="bg-delete absolute -top-2 -right-2 rounded-2xl p-2 text-white">
                      <Trash />
                    </div>
                    <div class="absolute -bottom-2 -left-2 text-4xl">🛜</div>
                  </category-button>
                </template>
                <template v-else-if="task == null">
                  <category-button
                    :key="'shared-missing-' + String(task) + '-' + id"
                    icon="❓"
                    class="shake m-4"
                    @click="deleteSharedTask(id)"
                  >
                    <div class="bg-delete absolute -top-2 -right-2 rounded-2xl p-2 text-white">
                      <Trash />
                    </div>
                    <div class="absolute -bottom-2 -left-2 text-4xl">🛜</div>
                  </category-button>
                </template>
              </template>
            </template>
            <category-button :icon="ICON_PLUS" class="m-4" @click="addTask" />
            <category-button
              icon="🛜"
              class="relative m-4"
              :class="{ 'opacity-50': !isOnline }"
              @click="addSharedTask"
            >
              <div v-if="!isOnline" class="absolute -top-2 -right-2 rounded-full bg-yellow-500 p-1">
                <WifiOff class="h-4 w-4 text-white" />
              </div>
            </category-button>
          </template>
          <template v-else>
            <template v-for="id in category.taskIds">
              <template v-for="task in (tasks as Task[]).filter((e) => e.id == id)">
                <category-button
                  v-if="task.cooldown > 0"
                  :key="task.id"
                  :icon="task.icon"
                  class="m-4"
                  :value="
                    !task.isCooldownReversed
                      ? task.cooldown - (Date.now() - task.lastUsed) / 1000
                      : (Date.now() - task.lastUsed) / 1000
                  "
                  :max="task.cooldown"
                  :disable-lines="true"
                  :limit="task.expiresIn"
                  @click="onTaskClick(task, null, $event)"
                />
                <category-button
                  v-else
                  :key="task.id + '-no-cooldown'"
                  :limit="task.expiresIn"
                  :icon="task.icon"
                  class="m-4"
                  @click="onTaskClick(task, null, $event)"
                />
              </template>
            </template>
            <template v-for="id in category.sharedTaskIds">
              <template v-for="task in [Task.fetch(db, id)]">
                <template v-if="task">
                  <category-button
                    v-if="task.value.cooldown > 0"
                    :key="'shared-' + task.value.id"
                    :icon="task.value.icon"
                    class="m-4"
                    :value="
                      !task.value.isCooldownReversed
                        ? task.value.cooldown - (Date.now() - task.value.lastUsed) / 1000
                        : (Date.now() - task.value.lastUsed) / 1000
                    "
                    :max="task.value.cooldown"
                    :disable-lines="true"
                    :limit="task.value.expiresIn"
                    @click="onTaskClick(task.value, id, $event)"
                  />
                  <category-button
                    v-else
                    :key="'shared-' + task.value.id + '-no-cooldown'"
                    :limit="task.value.expiresIn"
                    :icon="task.value.icon"
                    class="m-4"
                    @click="onTaskClick(task.value, id, $event)"
                  />
                </template>
              </template>
            </template>
          </template>
        </div>

        <!-- Offline mode indicator for category editing -->
        <div v-if="!isOnline" class="mt-4 mb-8 flex justify-center">
          <div
            class="flex max-w-[90%] items-start rounded-md border-l-4 border-yellow-500 bg-yellow-100 p-4 text-yellow-700"
          >
            <WifiOff class="mr-2 h-5 w-5 flex-shrink-0" />
            <div>
              <p class="font-medium">You're currently offline</p>
              <p class="text-sm">
                Changes to this category will be saved locally and synced when your connection is
                restored.
              </p>
              <p v-if="editMode" class="mt-1 text-sm">
                <strong>Note:</strong> Adding shared tasks requires an internet connection.
              </p>
            </div>
          </div>
        </div>

        <div v-if="editMode" class="flex justify-center">
          <button
            class="text-delete font-fredoka absolute bottom-2 flex cursor-pointer items-center rounded-xl bg-pink-800 px-4 py-2 text-lg hover:bg-pink-600 active:bg-pink-200"
            @click="deleteCategory"
          >
            Delete Category
            <WifiOff v-if="!isOnline" class="ml-2 h-4 w-4" />
          </button>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import { Trash, WifiOff } from 'lucide-vue-next';
import { isOnline } from '../composable/useNetworkStatus.ts';
import { IonContent, IonPage, onIonViewWillEnter } from '@ionic/vue';
import RoundHeader from '../components/RoundHeader.vue';
import {
  deleteAndSync,
  saveAndSync,
  usePreferencesItem,
} from '../composable/usePreferenceCollection.ts';
import { Category } from '../models/Category.ts';
import { useRoute } from 'vue-router';
import Fill from '../components/Fill.vue';
import TinyButton from '../components/TinyButton.vue';
import { ICON_MINUS, ICON_PLUS, icons, uploadFileToFolder } from '../services/Directus.ts';
import CategoryButton from '../components/CategoryButton.vue';
import { Task } from '../models/Task.ts';
import { ref, type Ref, watch } from 'vue';
import { Score } from '../models/Score.ts';
import DirectusImage from '../components/DirectusImage.vue';
import router from '../router';
import confetti from 'canvas-confetti';
import { useCollection } from 'vuefire';
import { collection, getFirestore, getDoc, doc } from 'firebase/firestore';
import { userId } from '../services/FirebaseDB.ts';
import { Dialog } from '@capacitor/dialog';

const route = useRoute();
const category = usePreferencesItem(Category, route.params.categoryId as string);
const editMode: Ref<boolean> = ref<boolean>(false);
const showActivity: Ref<boolean> = ref<boolean>(false);
const progressBarRef: Ref<HTMLElement | null> = ref<HTMLElement | null>(null);

type FlyingTaskAnimation = {
  id: number;
  icon: string;
  isImage: boolean;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  phase: 'start' | 'fly' | 'pop';
};

const flyingTaskAnimations: Ref<FlyingTaskAnimation[]> = ref<FlyingTaskAnimation[]>([]);
let flyingTaskAnimationId = 0;

const db = getFirestore();
const tasks = useCollection(() =>
  userId.value ? collection(db, 'users', userId.value, 'tasks') : null
);

// Prompt user to select a photo (web-friendly). Returns File or null if cancelled
function pickImage(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    // On mobile browsers, hint to use the camera if available
    input.setAttribute('capture', 'environment');
    input.onchange = () => {
      const file = input.files && input.files.length > 0 ? input.files[0] : null;
      resolve(file);
    };
    input.click();
  });
}

const theme = () => {
  if (category.value == undefined) {
    return 'neutral';
  }
  const score = category.value.getScore() ?? 0;
  if (score > category.value.dailyGoal) {
    return 'amazing';
  } else if (score > 0) {
    return 'positive';
  } else {
    return 'neutral';
  }
};

function addToDailyGoal(value: number) {
  if (category.value == undefined) {
    return;
  }
  category.value.dailyGoal = Math.max(1, category.value.dailyGoal + value);
  saveAndSync(Category, category.value);
}

function setName(name: string) {
  if (category.value == undefined) {
    return;
  }
  category.value.name = name;
  saveAndSync(Category, category.value);
}

function setIcon(id: string) {
  if (category.value == undefined) {
    return;
  }
  category.value.icon = id;
  saveAndSync(Category, category.value);
}

function triggerTaskSparkles(x: number, y: number) {
  confetti({
    particleCount: 20,
    spread: 50,
    scalar: 0.6,
    ticks: 70,
    origin: { x: x / window.innerWidth, y: y / window.innerHeight },
    colors: ['#fde68a', '#fef9c3', '#ffffff'],
  });
}

function launchTaskToProgressAnimation(icon: string, event: MouseEvent) {
  if (!progressBarRef.value) {
    return;
  }

  const targetBounds = progressBarRef.value.getBoundingClientRect();
  const animationId = flyingTaskAnimationId++;
  const animation: FlyingTaskAnimation = {
    id: animationId,
    icon,
    isImage: icon?.length === 36,
    startX: event.clientX,
    startY: event.clientY,
    endX: targetBounds.left + targetBounds.width / 2,
    endY: targetBounds.top + targetBounds.height / 2,
    phase: 'start',
  };

  flyingTaskAnimations.value.push(animation);

  requestAnimationFrame(() => {
    const found = flyingTaskAnimations.value.find((entry) => entry.id === animationId);
    if (!found) {
      return;
    }
    found.phase = 'fly';
  });

  setTimeout(() => {
    const found = flyingTaskAnimations.value.find((entry) => entry.id === animationId);
    if (!found) {
      return;
    }
    found.phase = 'pop';
  }, 650);

  setTimeout(() => {
    triggerTaskSparkles(animation.endX, animation.endY);
    flyingTaskAnimations.value = flyingTaskAnimations.value.filter(
      (entry) => entry.id !== animationId
    );
  }, 830);
}

async function onTaskClick(task: Task, sharedPath: string | null, event: Event) {
  if (event instanceof MouseEvent) {
    launchTaskToProgressAnimation(task.icon, event);
  }
  await addScore(task, sharedPath);
}

async function addScore(task: Task, sharedPath: string | null = null) {
  if (category.value == undefined) {
    return;
  }

  // Create score instance first to possibly attach a photo
  const score = new Score(task);

  try {
    if (task.photoMode) {
      const file = await pickImage();
      if (!file) {
        // User cancelled selection; abort without side-effects
        return;
      }
      // Upload to Directus folder "Upload"
      const folderId = '824b54a8-9e88-4407-8cda-226df5cca73e';
      const uploadedId = await uploadFileToFolder(file, file.name || 'photo.jpg', folderId);
      score.photoId = uploadedId;
    }
  } catch (e) {
    console.error('Photo upload failed:', e);
    // If upload fails, abort adding the score to avoid inconsistent state
    return;
  }

  category.value.scores.push(score);
  task.lastUsed = Date.now();
  if (task.expiresIn) {
    task.expiresIn -= 1;
    if (task.expiresIn <= 0) {
      if (sharedPath == null) {
        await Task.fromPlain(task).delete(db);
        category.value.taskIds = category.value.taskIds.filter((e) => e != task.id);
        await saveAndSync(Category, category.value);
      }
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      });
    }
  }

  await Task.fromPlain(task).save(db, sharedPath);
  await saveAndSync(Category, category.value);
}

async function deleteCategory() {
  if (category.value == undefined) {
    return;
  }
  await deleteAndSync(Category, category.value.id);
  router.push('/home');
}

async function removeScore(score: Score) {
  if (category.value == undefined) {
    return;
  }
  category.value.scores = category.value.scores.filter((s) => s.id !== score.id);
  await saveAndSync(Category, category.value);
}

async function addTask() {
  if (!category.value) {
    return;
  }
  const task = new Task();
  await task.save(db);
  category.value.taskIds.push(task.id);
  await saveAndSync(Category, category.value);
}

async function addSharedTask() {
  if (!category.value) {
    return;
  }

  // Check if online before proceeding
  if (!isOnline.value) {
    await Dialog.alert({
      title: 'Offline Mode',
      message:
        "Adding shared tasks requires an internet connection. Please try again when you're back online.",
    });
    return;
  }

  const { value, cancelled } = await Dialog.prompt({
    title: 'Adding Shared Task',
    message: `Task ID:`,
  });

  if (cancelled) {
    return;
  }

  try {
    const parts = value.split('/');
    const friendId = parts[0];
    const taskId = parts[1];

    if (parts.length !== 2 || !friendId || !taskId) {
      await Dialog.alert({
        title: 'Invalid Format',
        message: 'Please enter a valid task ID in the format "userId/taskId"',
      });
      return;
    }

    const document = await getDoc(doc(db, 'users', friendId, 'tasks', taskId));
    const task = document.data();

    if (task?.public == true) {
      category.value.sharedTaskIds.push(value);
      category.value.sharedTaskIds = [...new Set(category.value.sharedTaskIds)];
      await saveAndSync(Category, category.value);
    } else {
      await Dialog.alert({
        title: 'Task Not Available',
        message: "This task either doesn't exist or is not set to public.",
      });
    }
  } catch (error) {
    console.error('Error adding shared task:', error);
    await Dialog.alert({
      title: 'Error',
      message: 'An error occurred while trying to add the shared task. Please try again.',
    });
  }
}

async function deleteSharedTask(value: string) {
  if (!category.value) {
    return;
  }

  category.value.sharedTaskIds = category.value.sharedTaskIds.filter((e) => e != value);
  await saveAndSync(Category, category.value);
}

let isSetup = false;
let confettiTriggered = false;
watch(category, () => {
  if (!category.value || isSetup) {
    return;
  }
  if (category.value.getScore() >= category.value.dailyGoal) {
    confettiTriggered = true;
  }

  watch(category.value, () => {
    if (!category.value || confettiTriggered) {
      return;
    }
    if (category.value.getScore() >= category.value.dailyGoal) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.4 },
      });
      confettiTriggered = true;
    }
  });
  isSetup = true;
});

onIonViewWillEnter(() => {
  editMode.value = false;
});
</script>

<style scoped></style>
